import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  QrCode, 
  LayoutDashboard, 
  UtensilsCrossed, 
  Sparkles, 
  BarChart3, 
  Settings, 
  CreditCard, 
  LogOut,
  Menu,
  X,
  Shield,
  Gift,
  Package,
  Rocket,
  Stamp,
  Grid3X3,
  ShoppingBag
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const { user, subscriptionStatus, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect to billing if subscription is pending or expired
  useEffect(() => {
    if ((subscriptionStatus === 'pending' || subscriptionStatus === 'expired') && location.pathname !== '/billing') {
      navigate('/billing');
    }
  }, [subscriptionStatus, location.pathname, navigate]);

  useEffect(() => {
    // Fetch current subscription to know if takeaway is available
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/subscriptions/current`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSubscriptionPlan(data.plan);
          if (data.subscription_status === 'trial' && data.trial_days_remaining != null) {
            setTrialInfo({ days: data.trial_days_remaining });
          }
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    };
    if (user) fetchSubscription();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Tableau de bord', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Menu', href: '/menu', icon: UtensilsCrossed },
    { name: 'Promotions', href: '/promotions', icon: Gift },
    { name: 'Assistant IA', href: '/ai', icon: Sparkles },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'Paramètres', href: '/settings', icon: Settings },
    { name: 'Abonnement', href: '/billing', icon: CreditCard },
    { name: 'Carte de fidélité', href: '/loyalty', icon: Stamp },
    { name: 'Commandes', href: '/orders', icon: ShoppingBag },
    { name: 'Tables', href: '/tables', icon: Grid3X3 },
    { name: 'Mise en avant', href: '/boost', icon: Rocket },
  ];

  // Add Takeaway for Premium users
  if (subscriptionPlan === 'premium') {
    navigation.splice(3, 0, { name: 'À emporter', href: '/takeaway', icon: Package });
  }

  if (user?.role === 'admin') {
    navigation.push({ name: 'Admin', href: '/admin', icon: Shield });
  }

  // Client-only navigation
  const clientNavigation = [
    { name: 'Mes cartes de fidélité', href: '/my-loyalty', icon: Stamp },
    { name: 'Explorer', href: '/explorer', icon: LayoutDashboard },
  ];

  const navItems = user?.role === 'user' ? clientNavigation : navigation;

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <QrCode className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-gray-900">ZOTMENU</span>
            </Link>
            <button 
              className="lg:hidden p-2 -mr-2"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-orange-50 text-orange-600'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                data-testid={`nav-${item.href.slice(1)}`}
              >
                <item.icon className={`h-5 w-5 ${isActive(item.href) ? 'text-orange-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-medium text-sm">
                {user?.email?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full justify-start text-gray-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="lg:hidden h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sticky top-0 z-30">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6 text-gray-600" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
              <QrCode className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-bold text-gray-900">ZOTMENU</span>
          </Link>
          <div className="w-10" /> {/* Spacer for centering */}
        </header>

        {/* Trial banner */}
        {trialInfo && user?.role === 'owner' && (
          <div
            className={`px-4 py-2.5 text-sm flex flex-wrap items-center justify-center gap-2 text-center ${
              trialInfo.days <= 7 ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'
            }`}
            data-testid="trial-banner"
          >
            <Gift className="h-4 w-4 flex-shrink-0" />
            <span>
              Essai gratuit : <strong>{trialInfo.days} jour{trialInfo.days > 1 ? 's' : ''} restant{trialInfo.days > 1 ? 's' : ''}</strong>
              {' '}· Ensuite 19,90€ HT/mois, sans engagement
            </span>
            <Link to="/billing" className="underline font-semibold hover:opacity-80" data-testid="trial-banner-link">
              Mon abonnement
            </Link>
          </div>
        )}

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
